import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  BasketContextActionEnum,
  useBasketContext,
  useSellarDistributionEligibility,
} from "../../../../hooks";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { Navigate, useMatch, useSearchParams } from "react-router";
import { ORDER_BUYER, SUPPLIER } from "../../../../constants/routes";
import { useSupplier } from "../../../../hooks/api/v2/supplier/useSupplier";
import { HttpError } from "~/utils/httpClient";
import { useSupplierStorefrontV2 } from "../../../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import {
  BusinessGetResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { useDraftOrders } from "../../../../hooks/api/v2/useDraftOrders";
import { usePaymentMethodList } from "../../../../hooks/api/v2/paymentMethod/usePaymentMethodList";
import { CardBrand } from "@stripe/stripe-js";
import { useStockListContext } from "../../useStockListContext";
import { CheckoutSelectedFulfillmentType } from "../..";
import {
  validFulfillmentTypes,
  CheckoutConnection,
  CheckoutContext,
  useConnectionForCheckout,
} from "./CheckoutProvider.shared";

export type PaymentMethod = {
  brand: CardBrand;
  last4Digits: string;
  stripePaymentMethodId: string;
};

export type CheckoutContextApi = {
  isLoading: boolean;
  supplier: SupplierGetResponseType | undefined;
  supplierUrlIdInParam: string;
  connection: CheckoutConnection | undefined;
  storefront: SupplierStorefrontV2GetResponseType | undefined;
  business: BusinessGetResponseType | undefined;
  priceTierLink: string | undefined;
  error: HttpError<unknown> | null;
  paymentMethods: PaymentMethod[];
  storefrontFulfillmentType: CheckoutSelectedFulfillmentType; // This is the fulfillment type from the storefront itself.
  checkoutFulfillmentType: CheckoutSelectedFulfillmentType; // This is the fulfillment type from the checkout flow. It changes whenever the user changes their fulfillment type while at checkout.
  clearBasket: () => void;
  updateFulfillmentType: (type: CheckoutSelectedFulfillmentType) => void;
  deleteDraftOrder: ReturnType<typeof useDraftOrders>["deleteOrder"];
};

CheckoutContext.displayName = "CheckoutContext";

type CheckoutProviderProps = {
  children: ReactNode;
};

export const CheckoutProvider = ({ children }: CheckoutProviderProps) => {
  const { checkoutSelectedFulfillmentType: storefrontFulfillmentType } =
    useStockListContext();
  const [searchParams] = useSearchParams();
  const { business: currentBusiness, loading: businessLoading } =
    useSellarCurrentBusiness();
  const supplierUrlIdInParam =
    useMatch({ path: SUPPLIER.pattern, end: false })?.params
      .supplierIdOrUrlId ?? "";
  const getSupplierAsync = useSupplier(supplierUrlIdInParam, {
    enabled: !!supplierUrlIdInParam,
  });
  const { deleteOrder } = useDraftOrders();
  const [basket, dispatchBasket] = useBasketContext();
  const supplierId = getSupplierAsync.data?.id ?? 0;
  const retailerId = currentBusiness?.Retailer?.id ?? 0;
  const priceTierLink =
    searchParams.get("storefrontLink") ??
    searchParams.get("priceTierLink") ??
    undefined;

  const getConnectionAsync = useConnectionForCheckout({
    supplierId: getSupplierAsync.data?.id ?? 0,
  });

  const [fulfillmentType, setFulfillmentType] =
    useState<CheckoutSelectedFulfillmentType>(storefrontFulfillmentType);

  const initialFulfillmentType =
    storefrontFulfillmentType ?? getConnectionAsync.data?.fulfillmentType;
  const validInitialFulfillmentType =
    initialFulfillmentType &&
    validFulfillmentTypes.includes(initialFulfillmentType)
      ? initialFulfillmentType
      : undefined;
  const [previousInitialFulfillmentType, setPreviousInitialFulfillmentType] =
    useState(validInitialFulfillmentType);
  if (validInitialFulfillmentType !== previousInitialFulfillmentType) {
    setPreviousInitialFulfillmentType(validInitialFulfillmentType);
    setFulfillmentType(
      (currentFulfillmentType) =>
        currentFulfillmentType ?? validInitialFulfillmentType,
    );
  }

  const supplierStorefrontQuery = useSupplierStorefrontV2(supplierId, {
    PriceTierLink: priceTierLink,
    fulfillmentType:
      fulfillmentType ?? getConnectionAsync.data?.fulfillmentType,
  });
  const getPaymentMethods = usePaymentMethodList({
    query: [
      {
        type: "SupplierId",
        modifier: "EQUALS",
        value: supplierId,
      },
    ],
  });

  const isLoading =
    businessLoading ||
    getSupplierAsync.isLoading ||
    getConnectionAsync.isLoading ||
    getPaymentMethods.isLoading;
  const error =
    getSupplierAsync.error ||
    supplierStorefrontQuery.error ||
    getConnectionAsync.error ||
    getPaymentMethods.error;

  const clearBasket = useCallback(() => {
    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: null,
    });

    deleteOrder({
      SupplierId: supplierId,
      RetailerId: retailerId,
      initiator: "retailer",
    });
  }, [supplierId, retailerId, deleteOrder, dispatchBasket]);

  const updateFulfillmentType = useCallback(
    (type: CheckoutSelectedFulfillmentType) => {
      if (!validFulfillmentTypes.includes(type)) return;

      setFulfillmentType(type);
    },
    [],
  );

  const value = useMemo(() => {
    const supplierData = getSupplierAsync.data;
    const connectionData = getConnectionAsync.data;
    const supplierStorefront = supplierStorefrontQuery.data;
    const paymentMethodsData =
      getPaymentMethods.data?.pages[0]
        ?.map((paymentMethod) => ({
          brand: (paymentMethod.brand ?? "unknown") as CardBrand,
          last4Digits: paymentMethod.last4 ?? "",
          stripePaymentMethodId:
            paymentMethod.Suppliers[0]?.stripePaymentMethodId ?? "",
        }))
        .filter((pm) => !!pm.stripePaymentMethodId) ?? [];

    return {
      isLoading,
      supplier: supplierData,
      connection: connectionData,
      storefront: supplierStorefront,
      paymentMethods: paymentMethodsData,
      business: currentBusiness ?? undefined,
      priceTierLink: priceTierLink,
      temporaryCartId: basket.temporaryCartId,
      supplierUrlIdInParam,
      error,
      storefrontFulfillmentType,
      checkoutFulfillmentType: fulfillmentType,
      clearBasket,
      updateFulfillmentType,
      deleteDraftOrder: deleteOrder,
    };
  }, [
    supplierUrlIdInParam,
    basket.temporaryCartId,
    getSupplierAsync.data,
    currentBusiness,
    getConnectionAsync.data,
    getPaymentMethods.data,
    supplierStorefrontQuery.data,
    isLoading,
    priceTierLink,
    error,
    storefrontFulfillmentType,
    fulfillmentType,
    updateFulfillmentType,
    clearBasket,
    deleteOrder,
  ]);

  // Redirect to storefront if SD-only supplier without delivery area
  const { isEligible: isEligibleForSdCheckout } =
    useSellarDistributionEligibility(supplierStorefrontQuery.data);
  const shouldRedirect = !isLoading && !isEligibleForSdCheckout;

  if (shouldRedirect) {
    return (
      <Navigate
        to={ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlIdInParam })}
        replace
      />
    );
  }

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
